import { type NotificationKind, useMarkNotificationsRead, useNotifications } from '@shkola/api';
import { useEffect } from 'react';
import { useAllMemberIds } from '../pages/NotificationsPage';
import type { NavKey } from './nav';

/**
 * Counts of what is unread at the items of the menu (UX of 26.10): the signal «загляни» instead of a
 * feed of news on the home page. Nothing goes away: the things stay in their sections, the bell keeps
 * every notification; opening a section only marks what was new in it as seen.
 */
const SECTION: Partial<Record<NotificationKind, NavKey>> = {
  grade: 'grades',
  homework: 'homework',
  homework_review: 'homework',
  quiz: 'quizzes',
  schedule: 'schedule',
  lesson: 'schedule',
  announcement: 'announcements',
  message: 'messages',
  event: 'events',
  club: 'clubs',
  meeting: 'meetings',
  form: 'forms',
  poll: 'polls',
  achievement: 'achievements',
  care: 'care',
};

/** Unread notifications by section of the menu. */
export function useNavBadges(): Partial<Record<NavKey, number>> {
  const notifications = useNotifications(useAllMemberIds());
  const counts: Partial<Record<NavKey, number>> = {};
  for (const n of notifications.data ?? []) {
    const key = SECTION[n.kind];
    if (key && !n.readAt) counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}

/** Opening a section marks its unread notifications as seen. */
export function useSeenSection(section: NavKey | undefined) {
  const notifications = useNotifications(useAllMemberIds());
  const { mutate } = useMarkNotificationsRead();
  const ids = section
    ? (notifications.data ?? [])
        .filter((n) => !n.readAt && SECTION[n.kind] === section)
        .map((n) => n.id)
    : [];
  const key = ids.join(',');
  useEffect(() => {
    if (key) mutate(key.split(','));
  }, [key, mutate]);
}
